import { createHash } from 'crypto';
import DOMPurify from 'dompurify';
import { emitTapNativeConversionEventIfApplicable } from '../helpers/tapNativeTracker';
import { BRAND_NAME, LEAD_REGISTRATION_API, isLive } from '../helpers/constants';
import { getCookieValue, setCookie } from '../helpers/cookie';

export function phoneValidator(elementFocused) {
  let phoneValidElem;
  let phoneInValidElem;
  if (elementFocused.id === 'demo-phone') {
    phoneValidElem = document.getElementById('demo-phone-valid');
    phoneInValidElem = document.getElementById('demo-phone-invalid');
  } else if (elementFocused.id === 'association-lead-phone') {
    phoneValidElem = document.getElementById('associate-phone-valid');
    phoneInValidElem = document.getElementById('associate-phone-invalid');
  } else if (elementFocused.id === 'btm-ppc-phone') {
    phoneValidElem = document.getElementById('ppc-phone-valid');
    phoneInValidElem = document.getElementById('ppc-phone-invalid');
  } else {
    phoneValidElem = document.getElementById('phone-valid');
    phoneInValidElem = document.getElementById('phone-invalid');
  }
  if (elementFocused.classList.contains('is-err')) {
    phoneInValidElem.setAttribute('style', 'display:block;');
    phoneValidElem.setAttribute('style', 'display:none;');
  } else {
    phoneInValidElem.setAttribute('style', 'display:none;');
    phoneValidElem.setAttribute('style', 'display:block;');
  }
}

function sanitizeInputs(leadInfo = {}) {
  Object.keys(leadInfo).forEach((key) => {
    const input = DOMPurify.sanitize(leadInfo[key].value);
    if (!input || input === '' || input === null) {
      // eslint-disable-next-line no-param-reassign
      leadInfo[key].valid = false;
    }
  });
}

export function isLeadInfoValid(leadInfo = {}) {
  if (leadInfo && Object.keys(leadInfo).length) {
    sanitizeInputs(leadInfo);
    return Object.values(leadInfo).every(value => value.valid);
  }
  return false;
}

export function showPlansFold(uniquePin) {
  const plansFold = document.querySelector('#plans-wrapper');
  const leadRegistrationFold = document.querySelector('#registration');
  const titlePlans = document.querySelector('#title-plans');
  const descPlans = document.querySelector('#desc-plans');

  const isUpTo500minsPlan = element => ['#planOneLink', '#planTwoLink', '#planThreeLink'].some(id => document.querySelector(id) === element);

  const giveDiscountForPartnershipPages = (signupLink) => {
    if (getCookieValue('isFromPartnershipPages') && !signupLink.includes('&customPlan=true&discountCode')) {
      // eslint-disable-next-line no-param-reassign
      signupLink += `&customPlan=true&discountCode=${isLive ? '5808' : '5014'}`;
    }
    return signupLink;
  };
  const updateLinkWithUniquePin = (element) => {
    if (element) {
      const originalLink = element.getAttribute('href');
      const updatedLink = originalLink.replace('directSignup=true', `leadUniquePin=${uniquePin}`);
      if (isUpTo500minsPlan(element) && getCookieValue('isFromPartnershipPages')) {
        element.setAttribute('href', giveDiscountForPartnershipPages(updatedLink));
      } else element.setAttribute('href', updatedLink);
    }
  };

  if (plansFold) {
    if (uniquePin) {
      ['#planOneLink', '#planTwoLink', '#planThreeLink', '#planFourLink', '#planFiveLink', '#planSixLink', '#planSevenLink', '#planEightLink', '#planNineLink'].forEach((selector) => {
        updateLinkWithUniquePin(document.querySelector(selector));
      });
    }
    plansFold.classList.remove('d-none');
  }
  // Hide the registration fold only when there is a plans fold to reveal in its place
  if (plansFold && leadRegistrationFold) {
    leadRegistrationFold.classList.add('d-none');
    if (titlePlans) titlePlans.classList.add('d-none');
    if (descPlans) descPlans.classList.add('d-none');
  }
}

// Removes the whole registration fold, including padded wrapper sections, to avoid empty space
export function removeLeadRegistrationFold() {
  document.querySelectorAll('[data-registration-fold]').forEach(fold => fold.remove());
}

export function setPhoneFlagAttribute() {
  const flagContainer = document.querySelector('.iti__flag-container');
  const selectedFlag = document.querySelector('.iti__selected-flag');
  if (flagContainer) {
    flagContainer.setAttribute('aria-label', 'Country code');
  }

  if (selectedFlag) {
    selectedFlag.setAttribute('role', 'button');
    selectedFlag.setAttribute('tabindex', '-1');
    selectedFlag.setAttribute('aria-haspopup', 'true');
    selectedFlag.setAttribute('aria-controls', 'country-listbox');
    selectedFlag.setAttribute('aria-label', 'Select country');
  }
}

function hash(string) {
  return createHash('sha256').update(string).digest('hex');
}

function pushRegistrationToGA4DataLayer(brandName, response) {
  if (window.dataLayer) {
    window.dataLayer.push({
      brand: brandName,
      source: 'internal',
      event: 'purchase',
      ecommerce: {
        transaction_id: response.UniquePIN,
        affiliation: 'Leads',
        value: 0.00,
        tax: 0.00,
        shipping: 0.00,
        currency: '',
        coupon: '',
        items: [{
          item_name: 'Leads',
          item_id: response.UniquePIN,
          Price: 0.00,
          item_brand: '',
          item_category: 'Website Leads',
          item_category_2: '',
          item_category_3: '',
          item_category_4: '',
          item_category_5: '',
          item_variant: '',
          quantity: 1,
        }],
      },
      contact_name: hash(response.fullName),
      contact_email: hash(response.email),
      contact_phone: hash(response.phone),
    });
  }
}

export function pushRegInfoToGTM(response) {
  window.localStorage.setItem(
    'gtmData',
    JSON.stringify([{
      source: response.source,
      city: response.city,
      state: response.state,
      country: response.country,
      name: response.fullName,
      phone: response.phone,
      email: response.email,
      medium: response.medium,
      campaign: response.campaign,
      term: response.term,
      gclid: response.gclid,
      event: 'Registration',
      transactionId: response.UniquePIN,
      transactionAffiliation: 'Registration',
      transactionTotal: 0,
      transactionShipping: 0,
      transactionTax: 0,
      transactionCurrency: 'USD',
      transactionProducts: [{
        sku: 'Lead',
        category: '',
        name: 'Lead',
        price: 0,
        quantity: 1,
      }],
    }]),
  );

  pushRegistrationToGA4DataLayer(BRAND_NAME, response);
}

export function getRefinedLeadInfo(leadInfo = {}) {
  return Object.values(leadInfo).reduce((prevValue, currentValue) => {
    const prevValueRef = prevValue;
    const { value, emittedBy } = currentValue;
    if (emittedBy === 'phone') {
      const { dialCode, nationalNumber } = currentValue;
      prevValueRef.countryCode = dialCode;
      prevValueRef.phone = nationalNumber;
    } else {
      prevValueRef[emittedBy] = value;
    }
    return prevValueRef;
  }, {});
}

export function highlightInvalidFields(leadInfo = {}) {
  Object.values(leadInfo).forEach((value) => {
    if (!value.valid && value.selector) {
      document.querySelector(value.selector).classList.add('is-err');
      if (value.selector === '#phone' || value.selector === '#demo-phone' || value.selector === '#association-lead-phone' || value.selector === '#btm-ppc-phone') {
        phoneValidator(document.querySelector(value.selector));
      }
    }
  });
}

export async function makeLeadRegistrationRequest(payload = {},
  successCallback = () => {},
  failureCallback = () => {}) {
  try {
    let response = await window.fetch(LEAD_REGISTRATION_API, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
    });
    response = await response.json();
    if (response && Object.keys(response).length) {
      const { UniquePIN, signupurl } = response;
      if (UniquePIN && signupurl) {
        pushRegInfoToGTM(response);
        setCookie('isregistered', true, 365);
        setCookie('uniquePin', UniquePIN, 365);
        emitTapNativeConversionEventIfApplicable();
        successCallback(response);
      } else {
        failureCallback(response);
      }
    }
  } catch (e) {
    console.error(e) // eslint-disable-line
    failureCallback();
  }
}
